import {useEffect,useRef,useState} from 'react';
import {doc,onSnapshot,setDoc,serverTimestamp} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {normalizeManagerLayout} from './managerBoardModel';
const read=key=>{try{return JSON.parse(localStorage.getItem(key));}catch{return null;}};
const cache=(key,record)=>{try{localStorage.setItem(key,JSON.stringify(record));}catch{/* Account saving still works when browser storage is disabled. */}};
const initial=()=>({config:normalizeManagerLayout(),dirty:false});
export default function useManagerLayout(uid) {
  const key='overview.manager.v1.'+(uid||'');
  const [state,setState]=useState(()=>({key,record:read(key)||initial(),ready:false,status:'loading'}));
  const records=useRef(new Map()),editedKeys=useRef(new Set());
  if(state.key===key)records.current.set(key,state.record);
  const scope=useRef(key);scope.current=key;
  const session=useRef({key,edited:false,remoteRevision:null});
  if(session.current.key!==key)session.current={key,edited:false,remoteRevision:null};
  const inFlight=useRef(new Set()),retryTimer=useRef(null),attempt=useRef(0);
  const [retry,setRetry]=useState(0);
  const retrySoon=()=>{
    if(retryTimer.current)return;
    retryTimer.current=setTimeout(()=>{retryTimer.current=null;setRetry(value=>value+1);},Math.min(60000,5000*2**Math.min(attempt.current++,4)));
  };
  useEffect(()=>{
    attempt.current=0;
    const reconnect=()=>{clearTimeout(retryTimer.current);retryTimer.current=null;setRetry(value=>value+1);};
    window.addEventListener('online',reconnect);window.addEventListener('focus',reconnect);
    return()=>{clearTimeout(retryTimer.current);retryTimer.current=null;window.removeEventListener('online',reconnect);window.removeEventListener('focus',reconnect);};
  },[key]);
  useEffect(()=>{
    let alive=true;
    const record=records.current.get(key)||read(key)||initial();
    setState({key,record,ready:false,status:record.dirty?'saving':'loading'});
    if(!uid)return undefined;
    // Include metadata changes so cached snapshots become server-confirmed even
    // when their data is identical. A cached read is not a failed account sync.
    const stop=onSnapshot(doc(db,'users',uid,'managerDashboards','main'),{includeMetadataChanges:true},snapshot=>{
      if(!alive||scope.current!==key||snapshot.metadata?.hasPendingWrites)return;
      const fromCache=!!snapshot.metadata?.fromCache,remote=snapshot.exists()?snapshot.data():null;
      if(!fromCache){session.current.remoteRevision=remote?.revision||null;attempt.current=0;clearTimeout(retryTimer.current);retryTimer.current=null;}
      setState(previous=>{
        if(previous.key!==key)return previous;
        if(fromCache&&previous.ready)return previous;
        const pending=previous.record;
        // Preserve edits made during this visit. On a later visit, prefer an
        // account layout changed elsewhere over stale, unsent cached edits.
        const keepPending=pending.dirty&&(session.current.edited||!remote||pending.baseRevision===(remote.revision||null));
        if(pending.dirty&&(fromCache||keepPending))return {...previous,ready:!fromCache,status:'saving'};
        const next=remote?{config:normalizeManagerLayout(remote),revision:remote.revision,dirty:false}:pending;
        cache(key,next);return {key,record:next,ready:!fromCache,status:fromCache?'loading':'saved'};
      });
    },()=>{if(alive&&scope.current===key){setState(previous=>previous.key===key?{...previous,ready:false,status:'pending'}:previous);retrySoon();}});
    return()=>{alive=false;stop();};
    // retrySoon uses refs so it can retain backoff across listener restarts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[key,uid,retry]);
  useEffect(()=>{
    if(!uid||state.key!==key||!state.ready||!state.record.dirty)return undefined;
    const record=state.record,token=key+'/'+record.revision;
    const timer=setTimeout(()=>{
      if(inFlight.current.has(token))return;
      inFlight.current.add(token);
      void setDoc(doc(db,'users',uid,'managerDashboards','main'),{...record.config,revision:record.revision,updatedAt:serverTimestamp()})
        .then(()=>{if(scope.current===key){session.current.remoteRevision=record.revision;setState(previous=>{
          if(previous.key!==key||previous.record.revision!==record.revision)return previous;
          const next={...record,dirty:false};cache(key,next);return {key,record:next,ready:true,status:'saved'};
        });}}).catch(()=>{if(scope.current===key){setState(previous=>previous.key===key?{...previous,status:'pending'}:previous);retrySoon();}})
        .finally(()=>inFlight.current.delete(token));
    },500);
    return()=>clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[uid,key,state.key,state.ready,state.record,retry]);
  useEffect(()=>()=>{
    // Flush edits made on this visit if the user leaves before the debounce.
    const record=records.current.get(key)||read(key);
    if(!uid||!record?.dirty||!editedKeys.current.has(key)||inFlight.current.has(key+'/'+record.revision))return;
    void setDoc(doc(db,'users',uid,'managerDashboards','main'),{...normalizeManagerLayout(record.config),revision:record.revision,updatedAt:serverTimestamp()})
      .then(()=>{if(read(key)?.revision===record.revision)cache(key,{...record,dirty:false});}).catch(()=>{});
  },[key,uid]);
  return {config:normalizeManagerLayout(state.key===key?state.record.config:read(key)?.config),status:state.key===key?state.status:'loading',
    save:value=>{if(!uid)return;session.current.edited=true;const record={config:normalizeManagerLayout(value),dirty:true,baseRevision:session.current.remoteRevision,revision:window.crypto?.randomUUID?.()||Date.now()+'-'+Math.random()};
      editedKeys.current.add(key);records.current.set(key,record);cache(key,record);setState(previous=>({key,record,ready:previous.key===key&&previous.ready,status:'saving'}));}};
}
